/*
 * Member store cart attribute reconciler
 *
 * Single source of truth for the club member cart attributes. The per-club
 * order-type scripts (brook-hollow-order-type.js, southern-hills-order-type.js)
 * only TAG line items — they no longer touch cart attributes. This module owns
 * all attribute writes so the cart can never carry more than one club's data.
 *
 * The problem this solves: cart attributes are sticky. Once "Brook Hollow Member
 * Name" is set it stays set even after every Brook Hollow item is removed and a
 * Southern Hills item is added, so the order ended up carrying BOTH clubs'
 * attributes. See CHANGELOG for the exact payload that motivated this.
 *
 * Approach — after any cart change (and once on load) we read the authoritative
 * cart via /cart.js, decide the ACTIVE club from the line items actually present
 * (the _order_type property applied by the order-type scripts), and reconcile:
 *
 *   - Active club  -> its own attributes are left untouched (the member form owns
 *                     them) and the shared "order" attribute is set to its id.
 *   - Other clubs  -> every one of their attributes is cleared (set to "", which
 *                     removes it in Shopify).
 *   - No club      -> every club attribute AND "order" is cleared.
 *
 * Only attributes that actually differ from the current cart are written, so a
 * reconcile that finds nothing to change performs no write — that's what keeps
 * the fetch interceptor and pub/sub triggers from looping on our own writes.
 */
(function () {
  'use strict';

  // Line item property (and shared cart attribute value) that identifies a club.
  var ORDER_TYPE_KEY = '_order_type';
  // Shared cart attribute mirroring the active club id (kept for order context).
  var ORDER_ATTRIBUTE = 'order';

  // localStorage marker recording the club the shopper most recently engaged with
  // (set by the order-type scripts when a club collection/product page is viewed).
  // When a cart happens to contain more than one club's items, this marker decides
  // which single member form is shown and which club's attributes are kept.
  var ACTIVE_KEY = 'activeMemberStore';

  // Club registry. `id` matches the _order_type line item value; `attributeKeys`
  // lists EVERY cart attribute the club's member form can write, so we know which
  // keys to clear when the club is not the active one. Keep in sync with the keys
  // in sections/main-cart-footer.liquid.
  var CLUBS = [
    {
      id: 'brook-hollow-member',
      attributeKeys: [
        'Brook Hollow Member Name',
        'Brook Hollow Member Number',
        'Brook Hollow Authorization',
      ],
    },
    {
      id: 'southern-hills-member',
      attributeKeys: [
        'Southern Hills Member Name',
        'Southern Hills Member Number',
        'Southern Hills Authorization',
      ],
    },
  ];

  // Unwrapped fetch captured at install time. Our own cart reads/writes go through
  // this so they bypass our interceptor and can't retrigger a reconcile on
  // themselves.
  var originalFetch = null;
  var reconcileTimer = null;

  function cartUrl() {
    return (window.routes && window.routes.cart_url) || '/cart';
  }

  function cartUpdateUrl() {
    return (window.routes && window.routes.cart_update_url) || '/cart/update.js';
  }

  function doFetch() {
    var f = originalFetch || window.fetch;
    return f.apply(window, arguments);
  }

  function findClubById(id) {
    if (!id) return null;
    for (var i = 0; i < CLUBS.length; i++) {
      if (CLUBS[i].id === id) return CLUBS[i];
    }
    return null;
  }

  // ---- Active club marker (localStorage) ----------------------------------

  function getActiveId() {
    try {
      var raw = window.localStorage.getItem(ACTIVE_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      return (data && data.id) || null;
    } catch (e) {
      return null;
    }
  }

  function setActiveId(id) {
    if (!findClubById(id)) return;
    try {
      window.localStorage.setItem(ACTIVE_KEY, JSON.stringify({ id: id, ts: new Date().getTime() }));
    } catch (e) {
      /* storage unavailable — fail silently */
    }
    // Reflect immediately if we happen to be on a page showing the forms.
    try {
      toggleForms();
    } catch (e) {
      /* forms not present — ignore */
    }
  }

  // Exposed so the per-club order-type scripts can flag their club as the most
  // recently active one when their collection/product page is viewed.
  window.MemberStoreActive = { get: getActiveId, set: setActiveId };

  // ---- Active club resolution ---------------------------------------------

  // Distinct club ids present as line items in the cart, in cart order.
  function presentClubIdsFromCart(cart) {
    var items = (cart && cart.items) || [];
    var ids = [];
    items.forEach(function (item) {
      var props = item && item.properties;
      var id = props && props[ORDER_TYPE_KEY];
      if (findClubById(id) && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  // Given the club ids available (in cart or rendered on the page), pick the single
  // active one: the localStorage marker when it's among them, otherwise the first
  // available (so a single-club cart always resolves even without a marker).
  function resolveActiveId(availableIds) {
    if (!availableIds.length) return null;
    var marker = getActiveId();
    if (marker && availableIds.indexOf(marker) !== -1) return marker;
    return availableIds[0];
  }

  // Build the { key: value } map of attributes that need to change to bring the
  // cart in line with the active club. Empty object => nothing to do.
  function computeUpdates(cart) {
    var active = findClubById(resolveActiveId(presentClubIdsFromCart(cart)));
    var current = (cart && cart.attributes) || {};
    var updates = {};

    CLUBS.forEach(function (club) {
      if (active && club.id === active.id) return; // keep the active club's fields
      club.attributeKeys.forEach(function (key) {
        var value = current[key];
        if (value !== undefined && value !== null && value !== '') updates[key] = '';
      });
    });

    var desiredOrder = active ? active.id : '';
    if ((current[ORDER_ATTRIBUTE] || '') !== desiredOrder) {
      updates[ORDER_ATTRIBUTE] = desiredOrder;
    }

    return updates;
  }

  function writeAttributes(updates) {
    var keys = Object.keys(updates);
    if (!keys.length) return;
    try {
      doFetch(cartUpdateUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: updates }),
      }).catch(function () {
        /* network error — fail silently */
      });
    } catch (e) {
      /* fetch unavailable — fail silently */
    }
  }

  // Read the authoritative cart and reconcile its attributes. Reconcile from a cart
  // object directly when the caller already has one (avoids the extra request).
  function reconcile(cart) {
    if (cart && cart.items) {
      writeAttributes(computeUpdates(cart));
      return;
    }
    try {
      doFetch(cartUrl() + '.js', {
        method: 'GET',
        headers: { Accept: 'application/json' },
      })
        .then(function (r) {
          return r && r.ok ? r.json() : null;
        })
        .then(function (data) {
          if (data) writeAttributes(computeUpdates(data));
        })
        .catch(function () {
          /* unreadable — ignore */
        });
    } catch (e) {
      /* fetch unavailable — ignore */
    }
  }

  // ---- Single-form visibility ---------------------------------------------

  // The member forms rendered on the page, each paired with its club id.
  function renderedForms() {
    var out = [];
    CLUBS.forEach(function (club) {
      var el = document.querySelector('[data-club-id="' + club.id + '"]');
      if (el) out.push({ id: club.id, el: el });
    });
    return out;
  }

  // Show or hide a form. A hidden form also has its inputs disabled so its
  // required fields are neither submitted with the cart nor block checkout
  // (the form JS additionally treats disabled fields as valid). After toggling,
  // re-run the form element's checkout-block evaluation so a deactivated form
  // releases its hold on the shared dynamic-checkout buttons (Shop Pay / PayPal).
  function activateForm(el, active) {
    el.style.display = active ? '' : 'none';
    var controls = el.querySelectorAll('input, textarea, select');
    Array.prototype.forEach.call(controls, function (control) {
      control.disabled = !active;
    });
    if (typeof el.refreshBlockState === 'function') el.refreshBlockState();
  }

  // When a cart contains items from more than one club (an edge the client says
  // shouldn't happen, but which we must not mis-handle), show only the most
  // recently active club's form and deactivate the rest.
  function toggleForms() {
    var forms = renderedForms();
    if (!forms.length) return;
    if (forms.length === 1) {
      activateForm(forms[0].el, true); // single form is always active
      return;
    }
    var activeId = resolveActiveId(
      forms.map(function (f) {
        return f.id;
      })
    );
    forms.forEach(function (f) {
      activateForm(f.el, f.id === activeId);
    });
  }

  // Collapse the interceptor + pub/sub triggers for a single mutation into one
  // reconcile pass.
  function scheduleReconcile() {
    if (reconcileTimer) clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(function () {
      reconcileTimer = null;
      reconcile(null);
    }, 50);
  }

  // Endpoints that can change which club is active: an add, a line item change
  // (quantity/removal), or a clear. Attribute-only /cart/update writes are
  // intentionally excluded so member-form typing doesn't trigger reconciles.
  function isActiveChangingUrl(url) {
    return (
      !!url &&
      (url.indexOf('/cart/add') !== -1 ||
        url.indexOf('/cart/change') !== -1 ||
        url.indexOf('/cart/clear') !== -1)
    );
  }

  function installCartInterceptor() {
    if (typeof window.fetch !== 'function' || window.__memberStoreCartHook) return;
    window.__memberStoreCartHook = true;

    originalFetch = window.fetch;
    window.fetch = function (input, options) {
      var url = typeof input === 'string' ? input : input && input.url;
      var relevant = isActiveChangingUrl(url);
      var result = originalFetch.apply(this, arguments);

      if (relevant && result && typeof result.then === 'function') {
        result
          .then(function (response) {
            if (response && response.ok) scheduleReconcile();
          })
          .catch(function () {});
      }

      return result;
    };
  }

  // Dawn publishes cart-update after mutations through its own components (cart
  // page, drawer, quick-add, product form). Covers flows the interceptor might
  // miss and vice versa — both funnel into the same debounced reconcile.
  function installCartUpdateListener() {
    if (typeof subscribe !== 'function') return;
    var eventName =
      (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) ||
      'cart-update';
    subscribe(eventName, function () {
      scheduleReconcile();
      // Dawn re-renders the cart footer (and its forms) on update, so re-apply
      // single-form visibility to the fresh DOM.
      toggleForms();
    });
  }

  function init() {
    installCartInterceptor();
    installCartUpdateListener();
    // Show only the active club's form when more than one is rendered.
    toggleForms();
    // Reconcile once on load so a cart left stale by a previous session (or a flow
    // that produced no event) is cleaned up before the shopper reaches checkout.
    reconcile(null);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
